import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { PageHead, useResource } from '../components/ui';
import Overview from './cookies/Overview';
import Inventory from './cookies/Inventory';
import Scans from './cookies/Scans';
import Policy from './cookies/Policy';
import Banner from './cookies/Banner';
import Complaint from './cookies/Complaint';
import type { PointRow, PurposeRow, View } from './cookies/types';
import '../styles/cookies.css';

/**
 * CM-10 cookie governance, under Collection › Website cookies.
 *
 * Five views on one page: the dashboard, the register and its review flow,
 * scans (scheduled and manual), the published cookie policy, and the banner
 * configurator with its region rules. The view is
 * kept in the address so a link or an alert can open the right one.
 */

const VIEWS: Array<[View, string]> = [
  ['overview', 'Overview'],
  ['cookies', 'Cookies'],
  ['scans', 'Scans'],
  ['policy', 'Policy'],
  ['banner', 'Banner'],
  ['complaint', 'Complaint check'],
];

export default function Cookies() {
  const [params, setParams] = useSearchParams();
  const requested = params.get('view') as View | null;
  const view: View = VIEWS.some(([k]) => k === requested) ? requested! : 'overview';
  const [days, setDays] = useState(30);
  const [reload, setReload] = useState(0);
  const [addingSchedule, setAddingSchedule] = useState(false);
  const [bannerDirty, setBannerDirty] = useState(false);
  const changed = () => setReload((n) => n + 1);

  const purposes = useResource<{ purposes: PurposeRow[] }>(() => api.get('/v1/purposes'), []);
  const points = useResource<{ collectionPoints: PointRow[] }>(() => api.get('/v1/collection-points'), []);
  const pointRows = points.data?.collectionPoints ?? [];

  const go = (v: View) => {
    if (v !== view && view === 'banner' && bannerDirty && !window.confirm('Unsaved banner changes are lost. Leave the banner?')) return;
    const next = new URLSearchParams(params);
    if (v === 'overview') next.delete('view'); else next.set('view', v);
    setParams(next, { replace: true });
    window.scrollTo({ top: 0 });
  };

  const scanner = can(getUser(), 'cookies:scan');

  return (
    <>
      {/* A sibling of the section header, so the shared rules hide the title and
          put the button on the tab row, as on every other tabbed page. */}
      <PageHead title="Website cookies">
        {view === 'scans' && scanner && <button className="primary" onClick={() => setAddingSchedule(true)}>Add schedule</button>}
      </PageHead>
      <div className="ck">

      <div className="q-bar">
        <div className="q-views" role="tablist" aria-label="Website cookies">
          {VIEWS.map(([k, label]) => (
            <button key={k} role="tab" aria-selected={view === k} className={view === k ? 'on' : ''} onClick={() => go(k)}>
              {label}
            </button>
          ))}
        </div>
        {view === 'overview' && (
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Period">
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        )}
      </div>

      {view === 'overview' && <Overview days={days} reload={reload} go={go} />}
      {view === 'cookies' && (
        <Inventory purposes={purposes.data?.purposes ?? []} reload={reload} onChanged={changed} go={go} />
      )}
      {view === 'scans' && (
        <Scans points={pointRows} reload={reload} onChanged={changed} adding={addingSchedule} setAdding={setAddingSchedule} />
      )}
      {view === 'policy' && <Policy points={pointRows} reload={reload} />}
      {view === 'banner' && <Banner points={pointRows} onChanged={() => { points.reload(); changed(); }} onDirty={setBannerDirty} />}
      {view === 'complaint' && <Complaint />}
      </div>
    </>
  );
}
